import { DEFAULT_VOLUMES } from '@hf/audio';
import { getPlatform, type KeyValueStorage } from '../platform';
import { cloneJson } from './clone';
import type { Settings } from './types';

export const SETTINGS_KEY = 'hf.settings.v1';

export const DEFAULT_SETTINGS: Settings = {
  version: 1,
  playerName: '',
  volumes: { ...DEFAULT_VOLUMES },
  screenShake: 1,
  crt: false,
  reducedMotion: false,
  inputDelay: 'auto',
  showStats: false,
  touchControls: 'auto',
  bindings: {},
  sessionToken: null,
  instantReplays: true,
};

export type { KeyValueStorage };

/** The platform's storage (web: localStorage) at call time. */
const defaultStorage = (): KeyValueStorage | null => getPlatform().storage();

/** Load settings, merging over defaults (unknown/corrupt data falls back to defaults). Never throws. */
export const loadSettings = (storage: KeyValueStorage | null = defaultStorage()): Settings => {
  try {
    const raw = storage?.getItem(SETTINGS_KEY);
    if (!raw) return cloneJson(DEFAULT_SETTINGS);
    const parsed = JSON.parse(raw) as Partial<Settings>;
    if (parsed.version !== 1) return cloneJson(DEFAULT_SETTINGS);
    return {
      ...cloneJson(DEFAULT_SETTINGS),
      ...parsed,
      volumes: { ...DEFAULT_SETTINGS.volumes, ...parsed.volumes },
    };
  } catch {
    return cloneJson(DEFAULT_SETTINGS);
  }
};

export const saveSettings = (s: Settings, storage: KeyValueStorage | null = defaultStorage()): void => {
  try {
    storage?.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    /* storage full / blocked: settings stay in memory */
  }
};
